import { useValue } from "@legendapp/state/react";
import { Icon } from "@repo/ui/components/icon";
import { Button } from "@repo/ui/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@repo/ui/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@repo/ui/ui/dropdown-menu";
import { Input } from "@repo/ui/ui/input";
import { Spinner } from "@repo/ui/ui/spinner";
import { Switch } from "@repo/ui/ui/switch";
import { type ReactElement, useState } from "react";
import { toast } from "sonner";

import { captureEvent } from "~/libs/posthog.lib";
import { user$ } from "~/stores/auth";
import { boardInvitations$, inviteToBoard } from "~/stores/board-invitations";
import { boardMembers$ } from "~/stores/board-members";
import { boards$ } from "~/stores/boards";

/**
 * Unified sharing dialog. One owner-only entry point combining:
 *
 * - **Anyone with the link** — toggles `boards.public` and `shareAccess`.
 * - **Invite people** — Phase 6 board-level invitations + member list.
 *
 * Other-org members see this dialog read-only (the form disables the
 * controls if the API rejects the mutation; we don't gate client-side).
 */
export const BoardShareDialog = ({
  boardId,
  children,
  open: controlledOpen,
  onOpenChange,
}: {
  boardId: string;
  children?: ReactElement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const showInviteSection = import.meta.env.DEV;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {children && <DialogTrigger render={children} />}
      <DialogContent
        className="sm:max-w-md"
        hideOverlay={false}
        overlayClassName="bg-black/40"
      >
        <DialogHeader className="gap-0">
          <DialogTitle className="font-semibold text-base">
            Share board
          </DialogTitle>
          <DialogDescription className="font-normal">
            {showInviteSection
              ? "Share this dashboard with anyone via link, or invite specific people by email."
              : "Share this dashboard with anyone via link."}
          </DialogDescription>
        </DialogHeader>
        <VerifyEmailNotice showInviteSection={showInviteSection} />
        <ShareLinkSection boardId={boardId} />
        {showInviteSection && <InvitePeopleSection boardId={boardId} />}
      </DialogContent>
    </Dialog>
  );
};

/**
 * Inline notice shown above share controls when the current user's
 * email is unverified. Better Auth's default invitation verification
 * requirement means recipients cannot open actions shared by unverified
 * users, so we hide controls behind a clear "verify first" message.
 */
const VerifyEmailNotice = ({
  showInviteSection,
}: {
  showInviteSection: boolean;
}) => {
  const verified = useValue(user$.emailVerified);
  if (verified === true) return null;
  return (
    <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-amber-700 text-xs dark:text-amber-300">
      {showInviteSection
        ? "Verify your email to invite people or share a public link to this board."
        : "Verify your email to share a public link to this board."}
    </div>
  );
};

const ShareLinkSection = ({ boardId }: { boardId: string }) => {
  const isPublic = useValue(boards$[boardId].public);
  const shareAccess = useValue(boards$[boardId].shareAccess);
  const verified = useValue(user$.emailVerified);
  const disabled = verified !== true;
  // The access-mode selector (anonymous vs authenticated) is only
  // switchable in development; in production it stays locked.
  const canChangeAccess = import.meta.env.DEV;
  const url = `${window.location.origin}/boards/${boardId}`;

  const setPublic = (value: boolean) => {
    boards$[boardId].public.set(value);
    captureEvent("board_share_enabled", { boardId, enabled: value });
  };
  const setShareAccess = (value: "anonymous" | "authenticated") => {
    boards$[boardId].shareAccess.set(value);
    captureEvent("board_share_access_changed", { boardId, mode: value });
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard");
  };

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <span className="font-medium text-sm">Anyone with the link</span>
          <span className="font-normal text-muted-foreground text-xs">
            {isPublic
              ? shareAccess === "anonymous"
                ? "Anyone with the link can view this board."
                : "Any signed-in user with the link can view this board."
              : "Sharing is off. Only members can open the link."}
          </span>
        </div>
        <Switch
          checked={isPublic}
          onCheckedChange={setPublic}
          disabled={disabled}
        />
      </div>
      {isPublic && (
        <div className="flex flex-col gap-2 rounded-md border bg-muted/30 p-2">
          <div className="flex items-center gap-2">
            <Input value={url} readOnly className="text-xs" />
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopy}
              type="button"
            >
              <Icon name="link" />
              Copy
            </Button>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger
              disabled={!canChangeAccess}
              render={
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-start"
                  disabled={!canChangeAccess}
                >
                  <Icon
                    name={
                      shareAccess === "anonymous"
                        ? "network-globe"
                        : "person-circle"
                    }
                  />
                  <span className="text-xs">
                    {shareAccess === "anonymous"
                      ? "Anyone with the link"
                      : "Signed-in users only"}
                  </span>
                  {canChangeAccess && (
                    <Icon name="chevron-down" className="ml-auto" />
                  )}
                </Button>
              }
            />
            <DropdownMenuContent align="start">
              <DropdownMenuItem
                className="items-start"
                onClick={() => setShareAccess("anonymous")}
              >
                <Icon name="network-globe" />
                <div className="flex flex-col">
                  <span>Anyone with the link</span>
                  <span className="text-muted-foreground text-xs">
                    No sign-in required.
                  </span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="items-start"
                onClick={() => setShareAccess("authenticated")}
              >
                <Icon name="person-circle" />
                <div className="flex flex-col">
                  <span>Signed-in users only</span>
                  <span className="text-muted-foreground text-xs">
                    Visitors must sign in to view.
                  </span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </section>
  );
};

const InvitePeopleSection = ({ boardId }: { boardId: string }) => {
  const verified = useValue(user$.emailVerified);
  const disabled = verified !== true;
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  // Filter the org-wide stores down to this board. `useValue(() => ...)`
  // means the section only re-renders when the filtered slice's identity
  // actually changes, not on every other board's mutation.
  const pending = useValue(() =>
    Object.values(boardInvitations$.get() ?? {}).filter(
      (inv) => inv?.boardId === boardId && inv.status === "pending",
    ),
  );
  const members = useValue(() =>
    Object.values(boardMembers$.get() ?? {}).filter(
      (m) => m?.boardId === boardId,
    ),
  );

  const handleSend = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    setSending(true);
    try {
      const result = await inviteToBoard(boardId, trimmed);
      setEmail("");

      if (result.lifecycle === "resent") {
        if (result.emailDelivery === "sent") {
          toast.success(`Invitation re-sent to ${trimmed}`);
        } else {
          toast.error(
            "Invitation is still pending, but we couldn't resend the email. Ask them to use the original link or try again.",
          );
        }
      } else if (result.emailDelivery === "sent") {
        toast.success(`Invitation sent to ${trimmed}`);
      } else {
        toast.error(
          "Invitation created, but the email failed to send. Try inviting again to resend.",
        );
      }

      captureEvent("board_invite_sent", {
        boardId,
        lifecycle: result.lifecycle,
        emailDelivery: result.emailDelivery,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to invite";
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const handleCancel = (invitationId: string) => {
    boardInvitations$[invitationId].delete();
  };

  const handleRemove = (memberId: string) => {
    boardMembers$[memberId].delete();
  };

  return (
    <section className="flex flex-col gap-2 border-t pt-3">
      <div className="flex flex-col">
        <span className="font-medium text-sm">Invite people</span>
        <span className="font-normal text-muted-foreground text-xs">
          They'll get an email and view-only access. They don't join your
          workspace.
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Input
          type="email"
          placeholder="edward@graphicspress.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={disabled}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <Button
          onClick={handleSend}
          disabled={disabled || sending || !email.trim()}
        >
          {sending ? <Spinner className="size-3" /> : "Invite"}
        </Button>
      </div>
      {(pending.length > 0 || members.length > 0) && (
        <ul className="flex flex-col gap-1">
          {members.map((m) => (
            <li
              key={m.id}
              className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-muted/40"
            >
              <Icon
                name="person-circle"
                className="size-4 text-muted-foreground"
              />
              <span className="flex-1 truncate text-xs">
                {m.name || m.email || "Guest"}
              </span>
              <span className="text-muted-foreground text-xs">Viewer</span>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => handleRemove(m.id)}
                aria-label="Remove access"
              >
                <Icon name="cross" />
              </Button>
            </li>
          ))}
          {pending.map((p) => (
            <li
              key={p.id}
              className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-muted/40"
            >
              <Icon name="clock" className="size-4 text-muted-foreground" />
              <span className="flex-1 truncate text-xs">{p.email}</span>
              <span className="text-muted-foreground text-xs">Pending</span>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => handleCancel(p.id)}
                aria-label="Cancel invite"
              >
                <Icon name="cross" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
